import { Suspense, useDeferredValue, type KeyboardEvent } from 'react'
import { createFileRoute, Link, stripSearchParams, useMatch } from '@tanstack/react-router'
import { useSuspenseInfiniteQuery, useSuspenseQueries } from '@tanstack/react-query'
import * as valibot from 'valibot'
import { TRACE_SINCE, type TraceSince } from 'shared/wide-event-logs'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchInput } from '@/components/ui/search-input'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK, SettingsSection } from './-components/settings/settings-section'
import { WaypointsIcon, XIcon } from './-components/settings/settings-icons'
import { ADMIN_TONE } from './-components/admin/admin-pages'
import { SuperAdminBoundary } from './-components/admin/super-admin-boundary'
import {
  TraceDetailSkeleton,
  TRACES_DESCRIPTION,
  TracesListSkeleton,
  TracesPending,
} from './-components/admin/admin-pending'
import {
  CopyButton,
  formatMs,
  formatUtc,
  LINK_CLASS,
  SessionLink,
  StoreDownNotice,
  TraceLink,
  type TraceSummary,
} from './-components/admin/traces-shared'
import {
  traceListItemClass,
  TraceListItemContent,
  traceRootLabel,
  TraceWaterfall,
} from './-components/admin/trace-waterfall'

// The traces explorer: the list of traces on the left, the picked one's waterfall on the right. `?session=` narrows the
// list to one browser tab's traces.

const SEARCH_DEFAULTS = { since: '24h' as const, errors: false, q: '', wide: false, minMs: 0 as const }

const MIN_DURATIONS = [0, 100, 500, 1000, 3000] as const
type MinDuration = (typeof MIN_DURATIONS)[number]

export const Route = createFileRoute('/_app/admin/traces')({
  // Every filter, the session scope and the pick are search params: they key queries, and a view stays linkable.
  validateSearch: valibot.object({
    since: valibot.fallback(valibot.optional(valibot.picklist(TRACE_SINCE), '24h'), '24h'),
    errors: valibot.fallback(valibot.optional(valibot.boolean(), false), false),
    q: valibot.fallback(valibot.optional(valibot.string(), ''), ''),
    minMs: valibot.fallback(valibot.optional(valibot.picklist(MIN_DURATIONS), 0), 0),
    user: valibot.optional(valibot.string()),
    company: valibot.optional(valibot.string()),
    session: valibot.optional(valibot.string()),
    sessionAt: valibot.optional(valibot.string()),
    wide: valibot.fallback(valibot.optional(valibot.boolean(), false), false),
    trace: valibot.optional(valibot.string()),
    traceAt: valibot.optional(valibot.string()),
    span: valibot.optional(valibot.string()),
  }),
  search: { middlewares: [stripSearchParams(SEARCH_DEFAULTS)] },
  component: RouteComponent,
  pendingComponent: TracesPending,
  staticData: { fullWidth: true },
})

function useListContext() {
  return useMatch({ from: '/_app/admin/traces/_list' }).context
}

function RouteComponent() {
  const search = Route.useSearch()
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSection icon={WaypointsIcon} tone={ADMIN_TONE} title="Traces" description={TRACES_DESCRIPTION}>
        <SuperAdminBoundary what="traces">
          <div className="flex flex-col gap-4">
            {search.session ? <SessionScope sessionId={search.session} /> : <TraceFilterBar />}
            <Suspense fallback={<TracesListSkeleton />}>
              <TracesBody />
            </Suspense>
          </div>
        </SuperAdminBoundary>
      </SettingsSection>
    </div>
  )
}

// MARK: Filters

const SINCE_LABEL: Record<TraceSince, string> = {
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
}

function TraceFilterBar() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: search.q })
  return (
    <div className="flex flex-wrap items-center gap-3">
      <NativeSelect
        size="sm"
        aria-label="Show"
        value={search.errors ? 'errors' : 'all'}
        onChange={(event) =>
          void navigate({
            search: (prev) => ({ ...prev, errors: event.target.value === 'errors', trace: undefined }),
            replace: true,
          })
        }
      >
        <NativeSelectOption value="all">All traces</NativeSelectOption>
        <NativeSelectOption value="errors">With errors</NativeSelectOption>
      </NativeSelect>
      <NativeSelect
        size="sm"
        aria-label="Range"
        value={search.since}
        onChange={(event) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              since: event.target.value as TraceSince,
              trace: undefined,
            }),
            replace: true,
          })
        }
      >
        {TRACE_SINCE.map((since) => (
          <NativeSelectOption key={since} value={since}>
            {SINCE_LABEL[since]}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <NativeSelect
        size="sm"
        aria-label="Duration"
        value={String(search.minMs)}
        onChange={(event) =>
          void navigate({
            search: (prev) => ({ ...prev, minMs: Number(event.target.value) as MinDuration, trace: undefined }),
            replace: true,
          })
        }
      >
        {MIN_DURATIONS.map((minMs) => (
          <NativeSelectOption key={minMs} value={String(minMs)}>
            {minMs === 0 ? 'Any duration' : `At least ${formatMs(minMs)}`}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <div className="max-w-xs flex-1">
        <SearchInput
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Path, procedure or trace id…"
          aria-label="Search traces"
          className="h-9"
        />
      </div>
      {search.user && <FilterChip label="User" value={search.user} clear={{ user: undefined }} />}
      {search.company && <FilterChip label="Company" value={search.company} clear={{ company: undefined }} />}
    </div>
  )
}

function FilterChip(props: { label: string; value: string; clear: { user?: undefined; company?: undefined } }) {
  return (
    <span className="bg-muted flex items-center gap-1 rounded-md py-0.5 pr-0.5 pl-2 text-xs">
      {props.label} <span className="font-mono">{props.value}</span>
      <Link
        to="."
        search={(prev) => ({ ...prev, ...props.clear, trace: undefined })}
        replace
        aria-label={`Clear ${props.label.toLowerCase()} filter`}
        data-telemetry-label="clear-filter"
        className="text-muted-foreground hover:bg-background hover:text-foreground flex size-5 items-center justify-center rounded"
      >
        <XIcon className="size-3" />
      </Link>
    </span>
  )
}

// MARK: Session scope

/** The list narrowed to one browser tab's traces; what the tab did lives on the session's own page. */
function SessionScope(props: { sessionId: string }) {
  const search = Route.useSearch()
  // Without a known moment the server searched the last 7 days; widening is an explicit opt-in.
  const canWiden = !search.sessionAt && !search.wide
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm">
      <span className="flex items-center gap-1">
        Traces in session
        <SessionLink sessionId={props.sessionId} at={search.sessionAt} />
        <CopyButton value={props.sessionId} label="Copy session id" />
      </span>
      <span className="text-muted-foreground text-xs tabular-nums">
        {search.sessionAt && `within a day of ${formatUtc(search.sessionAt)}`}
        {search.wide && 'last 30 days'}
        {canWiden && (
          <>
            {'last 7 days · '}
            <Link to="." search={(prev) => ({ ...prev, wide: true })} className={LINK_CLASS}>
              Search 30 days
            </Link>
          </>
        )}
      </span>
      <Link
        to="/admin/traces"
        aria-label="Clear session filter"
        className="text-muted-foreground hover:bg-muted hover:text-foreground ml-auto flex size-7 items-center justify-center rounded-md"
      >
        <XIcon className="size-4" />
      </Link>
    </div>
  )
}

// MARK: List and detail

function TracesBody() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const context = useListContext()
  const deferred = useDeferredValue(context.tracesInfiniteQueryOptions)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } = useSuspenseInfiniteQuery(deferred)
  const { loadMoreRef } = useInfiniteScroll({ hasNextPage, isFetchingNextPage, fetchNextPage })
  const stale = isFetching || deferred !== context.tracesInfiniteQueryOptions

  const firstPage = data.pages[0]
  if (firstPage && !firstPage.ok) return <StoreDownNotice reason={firstPage.reason} />
  const traces = data.pages.flatMap((page) => (page.ok ? page.traces : []))

  const onListKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const step = { j: 1, ArrowDown: 1, k: -1, ArrowUp: -1 }[event.key]
    if (!step || event.metaKey || event.ctrlKey || event.altKey) return
    event.preventDefault()
    const index = traces.findIndex((trace) => trace.traceId === search.trace)
    const next = traces[index === -1 ? 0 : Math.max(0, Math.min(traces.length - 1, index + step))]
    if (!next) return
    void navigate({
      search: (prev) => ({ ...prev, trace: next.traceId, traceAt: next.startedAt, span: undefined }),
      replace: true,
    })
    event.currentTarget.querySelector<HTMLElement>(`[data-trace-id="${next.traceId}"]`)?.focus()
  }

  return (
    <div className="flex flex-col gap-2">
      {traces.length === 0 ? (
        <p className="text-muted-foreground rounded-xl border p-6 text-center text-sm">
          No traces match these filters.
        </p>
      ) : (
        <div
          className={cn(
            'flex flex-col overflow-hidden rounded-xl border transition-opacity lg:flex-row',
            stale && !isFetchingNextPage && 'opacity-60',
          )}
        >
          <div className="flex shrink-0 flex-col border-b lg:w-80 lg:border-r lg:border-b-0 xl:w-96">
            <LatencyStrip traces={traces} selectedId={search.trace} />
            <nav
              aria-label="Traces"
              onKeyDown={onListKeyDown}
              className="scrollbar max-h-[38dvh] overflow-y-auto lg:max-h-[70dvh]"
            >
              {traces.map((trace) => (
                <Link
                  key={trace.traceId}
                  to="."
                  search={(prev) => ({ ...prev, trace: trace.traceId, traceAt: trace.startedAt, span: undefined })}
                  replace
                  data-trace-id={trace.traceId}
                  aria-current={trace.traceId === search.trace ? 'true' : undefined}
                  className={traceListItemClass(trace.traceId === search.trace)}
                >
                  <TraceListItemContent trace={trace} />
                </Link>
              ))}
              {hasNextPage && <div ref={loadMoreRef} className="h-8" />}
            </nav>
          </div>
          <div className="min-w-0 flex-1">
            {search.trace ? (
              <Suspense fallback={<TraceDetailSkeleton />}>
                <TraceDetailPane traceId={search.trace} showSession={!search.session} />
              </Suspense>
            ) : (
              <p className="text-muted-foreground p-6 text-center text-sm">
                Pick a trace to see its waterfall.{' '}
                <span className="hidden lg:inline">j / k move through the list.</span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * The loaded traces' durations against their start, on a log scale, with their percentiles: where the slow ones
 * sit in time, at a glance, before reading the list.
 */
function LatencyStrip(props: { traces: TraceSummary[]; selectedId: string | undefined }) {
  const traces = props.traces
  const starts = traces.map((trace) => Date.parse(trace.startedAt))
  const from = Math.min(...starts)
  const width = Math.max(...starts) - from || 1
  const durations = traces.map((trace) => Math.max(trace.durationMs, 1)).sort((a, b) => a - b)
  const quantile = (q: number) => durations[Math.min(durations.length - 1, Math.floor(q * durations.length))] ?? 0
  const maxLog = Math.log10(Math.max(quantile(1), 10))
  return (
    <div className="flex flex-col gap-1 border-b px-3 py-2">
      <div aria-hidden className="relative h-10">
        {traces.map((trace, index) => {
          const x = (((starts[index] ?? from) - from) / width) * 100
          const y = (Math.log10(Math.max(trace.durationMs, 1)) / maxLog) * 100
          const selected = trace.traceId === props.selectedId
          return (
            <span
              key={trace.traceId}
              className={cn(
                'absolute size-1.5 -translate-x-1/2 translate-y-1/2 rounded-full',
                trace.errorCount > 0 ? 'bg-destructive' : 'bg-sky-500/70',
                selected && 'ring-foreground size-2.5 ring-2',
              )}
              style={{ left: `${x}%`, bottom: `${y}%` }}
            />
          )
        })}
      </div>
      <span className="text-muted-foreground flex gap-3 text-[11px] tabular-nums">
        <span>p50 {formatMs(quantile(0.5))}</span>
        <span>p95 {formatMs(quantile(0.95))}</span>
        <span>max {formatMs(quantile(1))}</span>
        <span className="ml-auto">{traces.length} loaded</span>
      </span>
    </div>
  )
}

function TraceDetailPane(props: { traceId: string; showSession: boolean }) {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const context = useListContext()
  const deferred = useDeferredValue(context.traceQueryOptions)
  const queries: NonNullable<typeof deferred>[] = deferred ? [deferred] : []
  const [result] = useSuspenseQueries({ queries })
  if (!result) return null
  if (!result.data.ok) return <StoreDownNotice reason={result.data.reason} />
  const spans = result.data.spans
  if (spans.length === 0) return <p className="text-muted-foreground p-4 text-sm">No spans found for this trace.</p>
  const label = traceRootLabel(spans) ?? props.traceId.slice(0, 8)
  const startedAt = search.traceAt ?? spans[0]?.occurredAt
  const sessionId = spans.find((span) => span.sessionId)?.sessionId
  return (
    <div className={cn('flex flex-col transition-opacity', deferred !== context.traceQueryOptions && 'opacity-60')}>
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <span className="min-w-0 truncate font-mono text-sm" title={props.traceId}>
          {label}
        </span>
        <CopyButton value={props.traceId} label="Copy trace id" />
        <span className="ml-auto flex items-center gap-3 text-xs">
          {startedAt && <span className="text-muted-foreground tabular-nums">{formatUtc(startedAt)}</span>}
          {props.showSession && sessionId && <SessionLink sessionId={sessionId} at={startedAt} />}
          <TraceLink traceId={props.traceId} at={startedAt} span={search.span}>
            Full view
          </TraceLink>
        </span>
      </div>
      {/* Keyed by trace: collapse, zoom and the span search belong to one trace. */}
      <TraceWaterfall
        key={props.traceId}
        spans={spans}
        selectedSpanId={search.span}
        onSelectSpan={(span) => void navigate({ search: (prev) => ({ ...prev, span }), replace: true })}
      />
    </div>
  )
}
